import React, { useState, useEffect } from 'react';
import TestCreatorView from './TestCreatorView';
import TestTakerView from './TestTakerView';
import PerformanceSummaryView from './PerformanceSummaryView';
import { leitnerService } from '../../services/leitnerService';

const TestCenterView = ({ questions, bookId, batchUpdateProgress }) => {
  const [tests, setTests] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [view, setView] = useState('list');
  const [selectedTest, setSelectedTest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const testsData = await leitnerService.getTests(bookId);
        setTests(testsData);

        const resultsData = await leitnerService.getTestResults(bookId);
        setTestResults(resultsData);
      } catch (error) {
        console.error('Failed to load test data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [bookId]);

  const addTest = async (newTest) => {
    try {
      const testWithId = await leitnerService.addTest(bookId, newTest);
      setTests(prev => [...prev, testWithId]);
      setView('list');
    } catch (error) {
      console.error('Failed to add test:', error);
    }
  };
  
  const addTestResult = async (newResult) => {
    try {
      const resultWithId = await leitnerService.saveTestResult(bookId, newResult);
      setTestResults(prev => [...prev, resultWithId]);
    } catch (error) {
      console.error('Failed to save test result:', error);
    }
  };

  const deleteTest = async (testId) => {
    if (window.confirm('Are you sure you want to delete this test? This will not delete past results.')) {
      try {
        await leitnerService.deleteTest(bookId, testId);
        setTests(prev => prev.filter(t => t.id !== testId));
      } catch (error) {
        console.error('Failed to delete test:', error);
      }
    }
  };

  const startTest = (test) => {
    setSelectedTest(test);
    setView('take');
  };
  
  const handleTestFinished = async (result, detailedResults) => {
    await addTestResult(result);
    
    const incorrectUpdates = Object.entries(detailedResults)
      .filter(([, answer]) => !answer.isCorrect)
      .map(([questionId, answer]) => ({
        questionId,
        newBox: 1,
        isSubQuestion: answer.isSubQuestion || false,
        parentQuestionId: answer.parentQuestionId || null
      }));

    if (incorrectUpdates.length > 0) {
      try {
        await batchUpdateProgress(incorrectUpdates);
      } catch (error) {
        console.error('Failed to update progress:', error);
      }
    }
    
    setView('list');
  };

  if (view === 'create') {
    const simpleQuestions = questions.filter(q => q.type === 'simple');
    return <TestCreatorView questions={simpleQuestions} addTest={addTest} onCancel={() => setView('list')} />;
  }

  if (view === 'take' && selectedTest) {
    const testQuestions = selectedTest.questionIds
      .map(id => questions.find(q => q.id === id))
      .filter(q => !!q && q.type === 'simple');
    
    if (testQuestions.length === 0) {
      return (
        <div className="text-center bg-slate-800 p-8 rounded-lg">
          <h2 className="text-2xl font-bold text-red-400">Error</h2>
          <p className="mt-2 text-slate-300">
            This test contains no valid questions. It's possible the original questions were deleted or are not simple questions.
          </p>
          <button 
            onClick={() => setView('list')} 
            className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-md"
          >
            Back to List
          </button>
        </div>
      );
    }

    return (
      <TestTakerView 
        test={selectedTest} 
        questions={testQuestions} 
        onFinish={handleTestFinished}
        onBackToList={() => setView('list')}
      />
    );
  }

  if (loading) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400">Loading test center...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-indigo-400">Available Tests</h2>
        <button 
          onClick={() => setView('create')} 
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition-transform transform hover:scale-105"
        >
          + Create New Test
        </button>
      </div>
      
      {tests.length === 0 ? (
        <div className="text-center bg-slate-800 p-8 rounded-lg">
          <p className="text-slate-400">No tests created yet. Click "Create New Test" to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tests.map(test => (
            <div key={test.id} className="bg-slate-800 p-4 rounded-lg flex justify-between items-center">
              <div>
                <h3 className="font-bold text-slate-200">{test.name}</h3>
                <p className="text-sm text-slate-400">{test.questionIds.length} questions</p>
              </div>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => deleteTest(test.id)} 
                  className="text-sm text-red-500 hover:text-red-400 font-semibold p-2 rounded-md hover:bg-slate-700 transition"
                >
                  Delete
                </button>
                <button 
                  onClick={() => startTest(test)} 
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md transition"
                >
                  Start Test
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <PerformanceSummaryView tests={tests} results={testResults} />
    </div>
  );
};

export default TestCenterView;